import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Checkbox from "@material-ui/core/Checkbox";
import Box from "@material-ui/core/Box";
import TablePagination from "@material-ui/core/TablePagination";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import DeleteIcon from "@material-ui/icons/Delete";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import EnhancedTableHead from "../../components/common/table/EnhancedTableHead";
import Layout from "../../components/common/hoc/Layout";
import RecordChangeButtons from "../../components/common/recordChangeButtons/RecordChangeButtons";
import CustomSpinner from "../../components/common/Spinner/CustomSpinner";
import { showCustomConfirmationDialog } from "../../store/customConfirmationDialogReducer";
import { showSnackbar } from "../../store/duck/uiReducer/uiReducer";
import Add from "./components/add";
import Edit from "./components/edit";
import NotFound from "../../components/common/notFound/NotFound";
import { getComparator, stableSort } from "../../utils/tableFilter";
import {
  listSchoolClass,
  addSchoolClass,
  updateSchoolClass,
  deleteSchoolClass,
} from "../../store/duck/schoolClass/schoolClass";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  table: {
    marginTop: "1rem",
    minWidth: 750,
  },
  rowEnable: {
    outlineColor: theme.palette.secondary.main,
    outlineWidth: "2px",
    outlineStyle: "solid",
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
  },
  buttonAdd: {
    marginLeft: theme.spacing(1),
  },
}));

const RegisterSchoolClass = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation("translation");
  const schoolClassReducer = useSelector((state) => state.schoolClassReducer);
  const { loading, error, schoolClass } = schoolClassReducer;
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [selected, setSelected] = useState([]);
  const [rowEnable, setRowEnable] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isEnabledFormAdd, setIsEnabledFormAdd] = useState(false);
  const [isEnabledFormEdit, setIsEnabledFormEdit] = useState(false);
  const [currentData, setCurrentData] = useState();

  const headCells = [
    {
      id: "periodo",
      numeric: false,
      disablePadding: true,
      label: t("schoolClass.headCells.period"),
    },
    {
      id: "nivelEscolar",
      numeric: false,
      disablePadding: false,
      label: t("schoolClass.headCells.schoolLevel"),
    },
    {
      id: "serie",
      numeric: false,
      disablePadding: false,
      label: t("schoolClass.headCells.schoolGrade"),
    },
    {
      id: "turma",
      numeric: false,
      disablePadding: false,
      label: t("schoolClass.headCells.schoolClass"),
    },
  ];

  const isSelected = (name) => selected.indexOf(name) !== -1;
  const emptyRows =
    rowsPerPage -
    Math.min(rowsPerPage, schoolClass?.length - page * rowsPerPage);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = schoolClass.map((n) => n);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleFormAdmin = () => {
    setIsEnabledFormAdd((prev) => !prev);
    setIsEnabledFormEdit(false);
  };

  const toggleFormEdit = (isEnable) => {
    setIsEnabledFormAdd(false);
    setIsEnabledFormEdit(isEnable);
  };

  const focusOnlastItemAdded = () => {
    setRowEnable(true);
    setTimeout(function () {
      setRowEnable(false);
    }, 3000);
  };

  const checkIfLastItem = (index, itemsSize) => {
    return index === itemsSize?.length - 1 && rowEnable;
  };

  const addHandler = async (formValues) => {
    await dispatch(
      addSchoolClass(
        formValues.period,
        formValues.schoolLevel,
        formValues.schoolGrade,
        formValues.schoolClass
      )
    );
    await dispatch(listSchoolClass());
    setSelected([]);
    focusOnlastItemAdded();
  };

  const deleteHandler = (schoolClass) => {
    const configDialog = {
      title: t("schoolClass.deleteDialog.title"),
      message: `${t("schoolClass.deleteDialog.firstMessage")} "${
        schoolClass.periodo
      }-${schoolClass.nivelEscolar}-${schoolClass.serie}", ${t(
        "schoolClass.deleteDialog.secondMessage"
      )}`,
      onSubmit: async function () {
        await dispatch(deleteSchoolClass(schoolClass))
          .then(async (result) => {
            dispatch(
              showSnackbar({
                message: "Classe removida com sucesso!",
                severity: "success",
                autoHideDuration: 3000,
              })
            );

            await dispatch({ type: "CUSTOM_CONFIRMATION_DIALOG_CLOSED" });
            await dispatch(listSchoolClass());
            setSelected([]);
          })
          .catch((err) => {
            dispatch(
              showSnackbar({
                message: "Erro ao remover classe!",
                severity: "error",
                autoHideDuration: 3000,
              })
            );

            dispatch({
              type: "SCHOOL_CLASS_DELETE_FAIL",
              payload: error,
            });
          });
      },

      close: async function () {
        await dispatch({ type: "CUSTOM_CONFIRMATION_DIALOG_CLOSED" });
      },
      buttons: {
        cancel: t("schoolClass.deleteDialog.buttons.cancel"),
        confirm: t("schoolClass.deleteDialog.buttons.confirm"),
      },
    };

    dispatch(
      showCustomConfirmationDialog({
        ...configDialog,
      })
    );
  };

  const editHandler = (schoolClass) => {
    toggleFormEdit(false);
    setCurrentData(schoolClass);
    const configDialog = {
      title: t("schoolClass.editDialog.title"),
      message: `${t("schoolClass.editDialog.firstMessage")} "${
        schoolClass.periodo
      }-${schoolClass.nivelEscolar}-${schoolClass.serie}",, ${t(
        "schoolClass.editDialog.secondMessage"
      )}`,
      onSubmit: function () {
        toggleFormEdit(true);
      },
      close: async function () {
        await dispatch({ type: "CUSTOM_CONFIRMATION_DIALOG_CLOSED" });
      },
      buttons: {
        cancel: t("schoolClass.editDialog.buttons.cancel"),
        confirm: t("schoolClass.editDialog.buttons.confirm"),
      },
    };
    dispatch(
      showCustomConfirmationDialog({
        ...configDialog,
      })
    );
  };

  const updateHandler = async (formValues) => {
    await dispatch(
      updateSchoolClass(
        currentData.id,
        formValues.period,
        formValues.schoolLevel,
        formValues.schoolGrade,
        formValues.schoolClass
      )
    );
    await dispatch(listSchoolClass());
    setSelected([]);
  };

  const formConfigI18n = {
    singleDeleteButton: t("schoolClass.singleDeleteButton"),
    singleDeleteMultipleButton: t("schoolClass.singleDeleteMultipleButton"),
    addForm: {
      title: t("schoolClass.addForm.title"),
      cancelButton: t("schoolClass.addForm.cancelButton"),
      confirmButton: t("schoolClass.addForm.confirmButton"),
      period: {
        requiredErrorMsg: t("schoolClass.addForm.period.requiredErrorMsg"),
        label: t("schoolClass.addForm.period.label"),
        placeholder: t("schoolClass.addForm.period.placeholder"),
      },
      schoolLevel: {
        requiredErrorMsg: t("schoolClass.addForm.schoolLevel.requiredErrorMsg"),
        label: t("schoolClass.addForm.schoolLevel.label"),
        placeholder: t("schoolClass.addForm.schoolLevel.placeholder"),
      },
      schoolGrade: {
        requiredErrorMsg: t("schoolClass.addForm.schoolGrade.requiredErrorMsg"),
        label: t("schoolClass.addForm.schoolGrade.label"),
        placeholder: t("schoolClass.addForm.schoolGrade.placeholder"),
      },
      schoolClass: {
        requiredErrorMsg: t("schoolClass.addForm.schoolClass.requiredErrorMsg"),
        label: t("schoolClass.addForm.schoolClass.label"),
        placeholder: t("schoolClass.addForm.schoolClass.placeholder"),
      },
    },
    editForm: {
      title: t("schoolClass.editForm.title"),
      cancelButton: t("schoolClass.editForm.cancelButton"),
      confirmButton: t("schoolClass.editForm.confirmButton"),
      period: {
        requiredErrorMsg: t("schoolClass.editForm.period.requiredErrorMsg"),
        label: t("schoolClass.editForm.period.label"),
        placeholder: t("schoolClass.editForm.period.placeholder"),
      },
      schoolLevel: {
        requiredErrorMsg: t(
          "schoolClass.editForm.schoolLevel.requiredErrorMsg"
        ),
        label: t("schoolClass.editForm.schoolLevel.label"),
        placeholder: t("schoolClass.editForm.schoolLevel.placeholder"),
      },
      schoolGrade: {
        requiredErrorMsg: t(
          "schoolClass.editForm.schoolGrade.requiredErrorMsg"
        ),
        label: t("schoolClass.editForm.schoolGrade.label"),
        placeholder: t("schoolClass.editForm.schoolGrade.placeholder"),
      },
      schoolClass: {
        requiredErrorMsg: t(
          "schoolClass.editForm.schoolClass.requiredErrorMsg"
        ),
        label: t("schoolClass.editForm.schoolClass.label"),
        placeholder: t("schoolClass.editForm.schoolClass.placeholder"),
      },
    },
  };

  useEffect(() => {
    dispatch(listSchoolClass());
  }, [dispatch]);

  return (
    <React.Fragment>
      <Layout title={t("schoolClass.title")}>
        <Container disableGutters maxWidth={false}>
          <Box
            component="span"
            display={isEnabledFormAdd || isEnabledFormEdit ? "none" : "block"}
          >
            <Grid item xs={12} sm={12} lg={12}>
              <div className={classes.buttons}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  onClick={() => setIsEnabledFormAdd((prev) => !prev)}
                >
                  {t("schoolClass.addButton")}
                </Button>
              </div>
            </Grid>
          </Box>

          <Add
            formConfigI18n={formConfigI18n}
            isEnabledFormAdd={!isEnabledFormAdd}
            addHandler={addHandler}
            show={toggleFormAdmin}
          />

          <Edit
            formConfigI18n={formConfigI18n}
            currentData={currentData}
            isEnabledFormEdit={!isEnabledFormEdit}
            loading={loading}
            editHandler={updateHandler}
            show={toggleFormEdit}
          />

          <>
            {loading ? (
              <CustomSpinner />
            ) : error ? (
              <NotFound
                title="Oops!"
                message="Ocorreu um erro, tente recarregar a página"
                image="/images/notfound.png"
                redirect="/app/content"
              />
            ) : (
              <div>
                <TableContainer>
                  <Table
                    className={classes.table}
                    aria-labelledby="tableTitle"
                    size={"medium"}
                    aria-label="enhanced table"
                  >
                    <EnhancedTableHead
                      headCells={headCells}
                      classes={classes}
                      numSelected={selected.length}
                      order={order}
                      orderBy={orderBy}
                      onSelectAllClick={handleSelectAllClick}
                      onRequestSort={handleRequestSort}
                      rowCount={schoolClass?.length}
                    />
                    <TableBody>
                      {stableSort(schoolClass, getComparator(order, orderBy))
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((row, index) => {
                          const isItemSelected = isSelected(row);
                          const labelId = `enhanced-table-checkbox-${index}`;

                          return (
                            <TableRow
                              hover
                              role="checkbox"
                              aria-checked={isItemSelected}
                              tabIndex={0}
                              key={row.id}
                              selected={isItemSelected}
                              className={
                                checkIfLastItem(index, schoolClass)
                                  ? classes.rowEnable
                                  : ""
                              }
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  onClick={(event) => handleClick(event, row)}
                                  checked={isItemSelected}
                                  inputProps={{ "aria-labelledby": labelId }}
                                />
                                <IconButton
                                  aria-label="edit"
                                  onClick={(event) => editHandler(row)}
                                >
                                  <EditIcon color="secondary" />
                                </IconButton>
                              </TableCell>
                              <TableCell padding="none">
                                {row.periodo}
                              </TableCell>
                              <TableCell align="left">
                                {row.nivelEscolar}
                              </TableCell>
                              <TableCell align="left">{row.serie}</TableCell>
                              <TableCell align="left">{row.turma}</TableCell>
                            </TableRow>
                          );
                        })}
                      {emptyRows > 0 && (
                        <TableRow style={{ height: 53 * emptyRows }}>
                          <TableCell colSpan={6} />
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box display="flex" p={1}>
                  <Box p={1} flexGrow={1}>
                    <TablePagination
                      rowsPerPageOptions={[10, 25]}
                      component="div"
                      count={schoolClass?.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onChangePage={handleChangePage}
                      onChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                  </Box>
                  <Box p={1} flexGrow={1}>
                    <RecordChangeButtons
                      singleDeleteButton={formConfigI18n.singleDeleteButton}
                      singleDeleteMultipleButton={
                        formConfigI18n.singleDeleteMultipleButton
                      }
                      itensSelected={selected}
                      handlerSingle={deleteHandler}
                      icon={<DeleteIcon />}
                    />
                  </Box>
                </Box>
              </div>
            )}
          </>
        </Container>
      </Layout>
    </React.Fragment>
  );
};

export { RegisterSchoolClass };
